import AppLayout from './AppLayout'
import './obs/obs.css'
import { usePageMeta } from './hooks/usePageMeta'
import { DOMAINE_PUBLIC, EMAIL_CONTACT, NOM_PRODUIT } from './constants'
import { mentionSiren, siegeAffichable, SOCIETE, statutJuridique, useSociete } from './societe'

export default function MentionsLegalesPage() {
  // Voir `useSociete` : `null` par défaut, donc « en cours d'immatriculation » tant
  // que le serveur n'a pas dit le contraire. Le jour du SIREN, cette page bascule seule.
  const { siren, siege } = useSociete()
  usePageMeta({
    title: `Mentions légales — ${NOM_PRODUIT}`,
    // Pas de STATUT juridique dans la méta-description (revue codex, PR #326). Le corps de la
    // page bascule avec le SIREN ; une description, elle, est mise en cache des semaines par
    // les moteurs et les aperçus de liens — elle aurait continué d'annoncer « en cours
    // d'immatriculation » bien après. Ce qui change n'a rien à faire dans ce qui se fige.
    description: `Mentions légales conformes LCEN art. 1-1 : éditeur ${SOCIETE.raisonSociale}, ` +
      "activité, propriété intellectuelle et droit des " +
      "bases de données, données personnelles, hébergeur.",
  })
  return (
    <AppLayout>
      <div className="obs2-jetons text-(--c-text) max-w-3xl mx-auto px-4 py-12">
        <h1 className="obs2-h1">Mentions légales</h1>
        <p className="text-sm text-(--c-muted) mb-10">
          Conformes à la loi pour la confiance dans l'économie numérique (LCEN) du 21 juin 2004, art. 1-1.
        </p>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Éditeur du site</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Dénomination</dt>
              <dd className="font-medium text-(--c-text)">{SOCIETE.raisonSociale}</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Statut juridique</dt>
              <dd className="font-medium text-(--c-text)">{statutJuridique(siren)}</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Capital social</dt>
              <dd className="font-medium text-(--c-text)">{SOCIETE.capital}</dd>
            </div>
            {/* Plus de crochet de réservation : « [en cours d'immatriculation] » se lisait
                comme un champ oublié. La phrase dit l'état réel, sans marqueur à remplir. */}
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">SIREN</dt>
              <dd className="font-medium text-(--c-text)">{mentionSiren(siren)}</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">RCS</dt>
              <dd className="font-medium text-(--c-text)">
                {siren
                  ? `RCS ${SOCIETE.greffe} ${siren.replace(/(\d{3})(?=\d)/g, '$1 ')}`
                  : `Greffe de ${SOCIETE.greffe} — immatriculation en cours`}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Siège social</dt>
              {/* Le siège est le domicile du président : il ne s'affiche qu'avec le SIREN,
                  quand il devient public au registre et que la loi l'exige (societe.ts). */}
              <dd className="font-medium text-(--c-text)">
                {siegeAffichable(siren, siege) ?? `${SOCIETE.greffe} — adresse publiée à l'immatriculation`}
              </dd>
            </div>
            {/* Gouvernance réelle (Marc 02/08) : Cyrille préside, Marc dirige — dans cet
                ordre, comme sur la landing. Le président d'une SAS est le représentant légal :
                c'est donc lui le directeur de la publication au sens de la LCEN. */}
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Président</dt>
              <dd className="font-medium text-(--c-text)">
                {SOCIETE.representant} — cofondateur et président, représentant légal
              </dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Directeur général</dt>
              <dd className="font-medium text-(--c-text)">Marc O'Flaherty — cofondateur et directeur général</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Directeur de la publication</dt>
              <dd className="font-medium text-(--c-text)">
                {`${SOCIETE.representant}, en qualité de ${SOCIETE.qualite.toLowerCase()}`}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Contact</dt>
              <dd className="font-medium text-(--c-text)">
                <a href={`mailto:${EMAIL_CONTACT}`} className="text-(--c-brand) hover:underline">{EMAIL_CONTACT}</a>
              </dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">TVA intracommunautaire</dt>
              <dd className="font-medium text-(--c-text)">
                {/* Déduit du SIREN (clé = (12 + 3 × (SIREN mod 97)) mod 97) : c'est le numéro de la
                    société, qui relève du régime réel depuis son immatriculation. Son activation
                    dans VIES par le service des impôts suit de quelques jours (24/10 : pas encore). */}
                FR41 131 499 741
              </dd>
            </div>
          </dl>
        </section>

        {/* PROPOSITION (Marc 02/08 : « une mention spéciale à l'entreprise, peut-être pas
            éditeur du site »). L'idée : au-delà de la formalité LCEN, dire ce qu'est la
            société et ce à quoi elle s'engage — c'est le même argument que la landing
            (« on répond, on corrige, on rend des comptes »), mais opposable ici. */}
        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">L'entreprise et son activité</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            {SOCIETE.raisonSociale} édite et exploite un <strong>observatoire territorial</strong> en mode SaaS :
            la société collecte les données publiques de référence produites par les
            administrations françaises (INSEE, DGFiP, DGCL, France Travail, ministères,
            agences), les structure, les actualise tous les mois, les met en perspective à l'échelle de chaque territoire et les commente.
            Le service s'adresse aux collectivités territoriales et à leurs groupements, dans le cadre de leurs missions.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Ce à quoi la société s'engage.</strong> Chaque indicateur publié affiche
            sa source et sa date. TerObs n'estime ni n'extrapole aucune valeur ; lorsqu'une source publie elle-même une estimation, elle est présentée comme telle. Une donnée
            manquante est signalée comme telle (« N/D ») plutôt que comblée. Toute erreur
            signalée à <a href={`mailto:${EMAIL_CONTACT}`} className="text-(--c-brand) hover:underline">{EMAIL_CONTACT}</a>{' '}
            est instruite, et corrigée à la source lorsqu'elle est avérée.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Ce que la société n'est pas.</strong> {SOCIETE.raisonSociale} n'est ni un service
            statistique public, ni une autorité de certification : elle republie et met en
            forme des données officielles dont les producteurs restent seuls responsables du
            contenu. Les analyses proposées sont un outil d'aide à la décision et ne se
            substituent ni à une instruction administrative, ni à un conseil juridique ou
            financier.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Propriété intellectuelle</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            L'ensemble des éléments composant le site {DOMAINE_PUBLIC} et la plateforme —{' '}
            <strong>textes, analyses rédigées, illustrations, graphiques, cartes, interfaces,
            méthodologies de calcul, nomenclatures, code source, marque et identité
            visuelle</strong> — est la propriété exclusive de {SOCIETE.raisonSociale} et protégé par le Code
            de la propriété intellectuelle. Aucune reproduction, représentation, adaptation ou
            diffusion, totale ou partielle, ne peut être faite sans autorisation écrite
            préalable.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Documents exportés.</strong> Les fichiers produits par la plateforme —
            exports <strong>PDF</strong>, tableaux, graphiques et jeux de données téléchargés —
            demeurent la propriété de {SOCIETE.raisonSociale}. L'abonnement confère au client une{' '}
            <strong>licence d'utilisation</strong> non exclusive et non cessible : il peut les
            exploiter librement pour ses propres besoins et ses missions (délibérations,
            rapports, conseil municipal ou communautaire, dossiers de subvention, transmission
            à ses autorités de tutelle et à ses prestataires), à l'exclusion de toute
            revente, rediffusion commerciale ou mise à disposition d'un tiers non concerné.
            L'attribution recommandée est : « Source : {NOM_PRODUIT} — Observatoire territorial ».
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Base de données.</strong> La collecte, la structuration, l'indexation, le
            croisement et la mise à jour des données représentent un investissement
            substantiel au sens des articles L. 341-1 et suivants du Code de la propriété
            intellectuelle. {SOCIETE.raisonSociale} revendique à ce titre la protection du{' '}
            <strong>droit sui generis du producteur de bases de données</strong>. Sont
            notamment interdits, sauf autorisation écrite : l'extraction ou la réutilisation
            d'une partie substantielle du contenu, la reconstitution de tout ou partie de la
            base, et l'aspiration automatisée (<em>scraping</em>). Toute atteinte engage la
            responsabilité de son auteur au titre du droit sui generis (art. L. 341-1 CPI), de
            la contrefaçon (art. L. 335-2 CPI), de la concurrence déloyale (art. 1240 du Code
            civil) et, le cas échéant, de l'accès frauduleux à un système de traitement
            automatisé de données (art. 323-1 du Code pénal).
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Données sources.</strong> Les données publiques mobilisées proviennent de
            producteurs officiels (INSEE, DGFiP, DGCL, SDES, ADEME, MENJ, SITADEL, cadastre,
            DVF, RNA…) et restent soumises aux licences de leurs producteurs respectifs
            (Licence Ouverte Etalab 2.0 ou équivalent). La valeur ajoutée de {SOCIETE.raisonSociale} réside
            dans leur agrégation, leur mise en cohérence et leur mise en perspective
            territoriale — c'est cette valeur ajoutée qui est protégée.
          </p>
          <p className="text-sm text-(--c-text) leading-relaxed mt-3">
            <strong>Logiciels tiers.</strong> Le site embarque des bibliothèques libres (React,
            MapLibre GL, Recharts…) dont les licences (MIT, ISC, BSD, OFL) exigent la
            reproduction de leur notice. Ces notices sont générées à chaque construction du site
            et consultables sur{' '}
            <a href="/licences-tiers.txt" className="text-(--c-brand) hover:underline font-medium">/licences-tiers.txt</a>.
          </p>
          <p className="text-sm text-(--c-muted) leading-relaxed mt-3">
            Le détail des usages autorisés et interdits figure à l'article 6 des{' '}
            <a href="/cgv" className="text-(--c-brand) hover:underline font-medium">conditions générales</a>.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Données personnelles</h2>
          <p className="text-sm text-(--c-text) leading-relaxed">
            Le traitement des données personnelles est détaillé dans notre <a href="/confidentialite" className="text-(--c-brand) hover:underline font-medium">politique de confidentialité</a>.
            Pour toute question RGPD : <a href={`mailto:${EMAIL_CONTACT}`} className="text-(--c-brand) hover:underline">{EMAIL_CONTACT}</a>.
          </p>
        </section>

        {/* Hébergement descendu en fin de page (Marc 02/08) : c'est une obligation LCEN, pas
            un argument de vente — et l'hébergeur est allemand. Mention conservée intégralement,
            simplement remise à sa place. */}
        <section className="obs2-card mb-6">
          <h2 className="obs2-section-title">Hébergement</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Hébergeur</dt>
              <dd className="font-medium text-(--c-text)">Hetzner Online GmbH</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Siège</dt>
              <dd className="font-medium text-(--c-text)">Industriestr. 25, 91710 Gunzenhausen, Allemagne</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Téléphone</dt>
              <dd className="font-medium text-(--c-text)">+49 (0)9831 505-0</dd>
            </div>
            <div className="flex">
              <dt className="w-40 text-(--c-muted)">Localisation données</dt>
              <dd className="font-medium text-(--c-text)">Centre de données de Nuremberg, Bavière, Allemagne (Union européenne)</dd>
            </div>
          </dl>
          <p className="text-xs text-(--c-muted) leading-relaxed mt-4">
            Toutes les données de nos clients sont hébergées en Europe, sous RGPD. La liste de nos
            sous-traitants figure dans la politique de confidentialité, article 6.
          </p>
        </section>

        <p className="text-xs text-(--c-muted) mt-8">
          Mentions légales mises à jour le 24 octobre 2026.
        </p>
      </div>
    </AppLayout>
  )
}
